import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import styled from 'styled-components';
import { Menu, X } from 'lucide-react';
import { CreateTicketModalProvider } from '../../context/CreateTicketModalContext';

const SIDEBAR_WIDTH = 260;
const EDGE_ZONE = 28;
const OPEN_THRESHOLD = SIDEBAR_WIDTH * 0.35;
const MOBILE_MQ = '(max-width: 768px)';

const LayoutWrapper = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: var(--color-bg-secondary);
  touch-action: pan-y;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 260px;
  min-height: 100vh;
  overflow-x: hidden;
  min-width: 0;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const MobileHeader = styled.header`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    position: sticky;
    top: 0;
    z-index: 90;
    padding: 0.75rem 1rem;
    background: var(--color-bg-primary);
    border-bottom: 1px solid var(--color-border-primary);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }
`;

const MenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 1px solid var(--color-border-primary);
  border-radius: 10px;
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  cursor: pointer;
  flex-shrink: 0;

  svg {
    font-size: 1.1rem;
  }
`;

const MobileTitle = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
    z-index: 110;
    opacity: ${({ $progress }) => $progress};
    pointer-events: ${({ $progress }) => ($progress > 0.02 ? 'auto' : 'none')};
    transition: ${({ $dragging }) => ($dragging ? 'none' : 'opacity 0.25s ease')};
  }
`;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches;

const AppLayout = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dragOffset, setDragOffset] = useState(null);
  const dragOffsetRef = useRef(null);
  const gestureRef = useRef({
    mode: null,
    startX: 0,
    startY: 0,
    locked: false,
  });

  const setDrag = useCallback((value) => {
    dragOffsetRef.current = value;
    setDragOffset(value);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
    setDrag(null);
  }, [location.pathname, setDrag]);

  useEffect(() => {
    if (!sidebarOpen && dragOffset == null) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [sidebarOpen, dragOffset]);

  useEffect(() => {
    const onTouchStart = (event) => {
      if (!isMobileViewport() || event.touches.length !== 1) return;
      const touch = event.touches[0];
      if (sidebarOpen) {
        gestureRef.current = {
          mode: 'close',
          startX: touch.clientX,
          startY: touch.clientY,
          locked: false,
        };
        return;
      }
      if (touch.clientX <= EDGE_ZONE) {
        gestureRef.current = {
          mode: 'open',
          startX: touch.clientX,
          startY: touch.clientY,
          locked: false,
        };
        return;
      }
      gestureRef.current = { mode: null, startX: 0, startY: 0, locked: false };
    };

    const onTouchMove = (event) => {
      const gesture = gestureRef.current;
      if (!gesture.mode || !isMobileViewport()) return;
      const touch = event.touches[0];
      if (!touch) return;

      const dx = touch.clientX - gesture.startX;
      const dy = touch.clientY - gesture.startY;

      if (!gesture.locked) {
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) {
          gesture.mode = null;
          setDrag(null);
          return;
        }
        if (Math.abs(dx) < 8) return;
        gesture.locked = true;
      }

      event.preventDefault();

      if (gesture.mode === 'open') {
        setDrag(clamp(-SIDEBAR_WIDTH + dx, -SIDEBAR_WIDTH, 0));
      } else if (gesture.mode === 'close') {
        setDrag(clamp(dx, -SIDEBAR_WIDTH, 0));
      }
    };

    const onTouchEnd = () => {
      const gesture = gestureRef.current;
      const offset = dragOffsetRef.current;
      gestureRef.current = { mode: null, startX: 0, startY: 0, locked: false };

      if (offset == null) return;

      const shouldOpen = offset > -SIDEBAR_WIDTH + OPEN_THRESHOLD;
      setSidebarOpen(shouldOpen);
      setDrag(null);
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [sidebarOpen, setDrag]);

  const progress =
    dragOffset != null
      ? (dragOffset + SIDEBAR_WIDTH) / SIDEBAR_WIDTH
      : sidebarOpen
        ? 1
        : 0;

  return (
    <CreateTicketModalProvider>
    <LayoutWrapper>
      <Overlay
        $progress={progress}
        $dragging={dragOffset != null}
        onClick={() => setSidebarOpen(false)}
        aria-hidden={progress < 0.02}
      />
      <Sidebar
        open={sidebarOpen}
        dragOffset={dragOffset}
        onClose={() => setSidebarOpen(false)}
      />
      <MainContent>
        <MobileHeader>
          <MenuButton
            type="button"
            onClick={() => setSidebarOpen((prev) => !prev)}
            aria-label={sidebarOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={sidebarOpen}
          >
            {sidebarOpen ? (
              <X size={18} strokeWidth={2} aria-hidden />
            ) : (
              <Menu size={18} strokeWidth={2} aria-hidden />
            )}
          </MenuButton>
          <MobileTitle>SupportHub</MobileTitle>
        </MobileHeader>
        {children}
      </MainContent>
    </LayoutWrapper>
    </CreateTicketModalProvider>
  );
};

export default AppLayout;
